import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "hd_session_id";

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("OPR") || ua.includes("Opera")) return "Opera";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Other";
}

function getCountryFromLanguage(): string | null {
  const lang = navigator.language || navigator.languages?.[0];
  if (!lang) return null;
  // Extract country code from locale like "sv-SE", "en-US", "da-DK"
  const parts = lang.split("-");
  return parts.length > 1 ? parts[1].toUpperCase() : parts[0].toUpperCase();
}

function getReferrer(): string | null {
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const url = new URL(ref);
    // Don't track self-referrals
    if (url.hostname === window.location.hostname) return null;
    return url.hostname;
  } catch {
    return null;
  }
}

export function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef<string>("");

  useEffect(() => {
    const path = location.pathname;
    
    // Don't double-track same page
    if (path === lastPath.current) return;
    lastPath.current = path;

    // Don't track admin pages
    if (path.startsWith("/admin") || path === "/security-monitor") return;

    const trackView = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const t0 = performance.now();
        await (supabase as any).rpc("record_page_view", {
          p_page_path: path,
          p_session_id: getSessionId(),
          p_device_type: getDeviceType(),
          p_browser: getBrowser(),
          p_country_code: getCountryFromLanguage(),
          p_referrer: getReferrer(),
          p_is_authenticated: !!session?.user,
        });
        const ms = Math.round(performance.now() - t0);
        // Anonym svarstidsmätning: ca 5 % av besöken, max en gång per minut per webbläsare
        const last = Number(localStorage.getItem("hd_perf_last") || 0);
        if (Math.random() < 0.05 && Date.now() - last > 60_000) {
          localStorage.setItem("hd_perf_last", String(Date.now()));
          (supabase as any).rpc("record_perf_sample", { p_duration_ms: ms, p_kind: "db" }).then(() => {}, () => {});
        }
      } catch {
        // Silent fail - analytics should never break the app
      }
    };

    // Small delay to not block rendering
    const timer = setTimeout(trackView, 500);
    return () => clearTimeout(timer);
  }, [location.pathname]);
}
